import { UserContext } from '@components/contexts/UserProvider';
import { formatToDecimals } from '@lib/helpers/formatting';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, ButtonToolbar, Col, Form, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import ExecutionSummaryCard from './ExecutionSummaryCard';
import OwnershipModal from './OwnershipModal';
import CustomSearch from './Sampling/CustomFilter/CustomSearch';
import { ArrowRight, IcoArrowClockwise, IcoDownloadCloudArrowSmall, IcoReported, IcoReportedPartially } from '@icons';
import ModalDownloadReport from './Sampling/ModalDownloadReport';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { Loading } from '@components/atomic/atoms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProjectResumeInvoices, getProjectResumeTotals, getSamplingHistory } from '@lib/api';
import OwnershipCellRenderer from './OwnershipCellRenderer';
import { columnCentered } from '@lib/helpers/agGridHelpers';
import AG_GRID_LOCALE_IT from '@components/ag-grid/locale-it';
import CustomPaginationMui from '@components/materialComponents/CustomPaginationMui';
import CustomFilter from './Sampling/CustomFilter/CustomFilter';
import { getUsersByProjectId } from '@lib/api_ts/usersApi';
import { parseQueryParams, wrapQueryParams } from './Sampling/CustomFilter/queryParamsUtils';
import ExecutionSummaryProgressCard from './ExecutionSummaryProgressCard';
import SamplingSetupModal from './SamplingSetupModal';
import FiltersToDisplay from './Sampling/CustomFilter/FiltersToDisplay';
import SamplingInfoBox from './SamplingInfoBox';
import SummaryInvoiceModal from './SummaryInvoiceModal';
import ResumeChecklistCellRenderer from './ResumeChecklistCellRenderer';
import SummaryContractModal from './SummaryContractModal';
import SummaryPaymentModal from './SummaryPaymentModal';
import SummaryFrameworkcontractModal from './SummaryFrameworkcontractModal';
import SummaryTenderModal from './SummaryTenderModal';
import TotalResumeChecklistCellRenderer from './TotalResumeChecklistCellRenderer';
import { getProjectTrackRecords } from '@lib/api_ts/trackRecords';
import { useHasPermission } from '@lib/hooks/useHasPermission';

type ExternalSupplyType = {
	key: string;
	hasDocuments?: boolean;
};

type MetaType = {
	meta: {
		total: number;
		per_page: number;
		current_page: number;
		last_page: number;
	};
};

type TotalsType = {
	total: number;
	totalRend: number;
	totalFt: number;
	totalRendFt: number;
	totalTs: number;
	totalRendTs: number;
};

type FilteredTotalType = {
	all: number;
	rend: number;
	totalFtIndirect: number;
	totalTsIndirect: number;
};

interface SummaryExternalSuppliesProps {
	project: {
		name: string;
		streamConfigurationType: string;
		sco: boolean;
		advancedConfig: { name: string; enabled: boolean }[];
		hasAssignedInvoices: boolean;
	};
}

export type StatisticsType = {
	checkAssignedCompleted: number;
	totalAssignedInvoices: number;
	percentageAssignedCompleted: number;
};

const SummaryExternalSupplies: React.FC<SummaryExternalSuppliesProps> = ({ /* totals,*/ project }) => {
	const { userAccess, myUser } = useContext(UserContext);
	const canViewMacroInfo = useHasPermission(['view-control-macro-information']);
	const searchRef = useRef(null);

	const externalSuppliesRef = useRef<AgGridReact>(null);

	const projectId = useParams<{ projectId: string }>().projectId;

	const pathname = useLocation().pathname;
	const completeListParam = `complete-list=true` as const;

	const { data: samplingHistory, isLoading: isLoadingSamplingHistory } = useQuery({
		queryKey: ['samplingHistory', projectId],
		queryFn: async () => {
			const result = await getSamplingHistory(projectId, 0);

			if (!result.success) {
				throw new Error('Failed to fetch sampling history');
			}

			const samplingList = result.data;
			const id = samplingList.find((el) => !el.completed)?.id ?? null;

			return { samplingList, id };
		},
		enabled: !!projectId,
	});

	const isActiveSampling = samplingHistory ? samplingHistory?.samplingList?.length > 0 : undefined;

	const { search } = useLocation();
	const isCompleteList = search.includes('complete-list=true');
	const [queryParams, setQueryParams] = useState<string | undefined | null>();
	const resetQueryParams = () => {
		setQueryParams(undefined);
	};

	const history = useHistory();

	const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});

	const { data: users, isPending: isPendingUsers } = useQuery({
		queryKey: ['usersPerProject', projectId],
		queryFn: () => getUsersByProjectId(projectId),
		enabled: !!projectId,
	});

	const myUserId = (() => {
		if (isPendingUsers) return undefined;
		const activeUser = users.find((user) => user.email === myUser.email);
		return activeUser ? activeUser.id : 'not_found';
	})();

	const handleAssignedToMe = (event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked;

		setActiveFilters((prevFilters) => {
			let newFilters;

			if (isChecked) {
				newFilters = {
					...prevFilters,
					assigned: [...(prevFilters.assigned || []), String(myUserId)],
				};
			} else {
				const updated = prevFilters.assigned?.filter((id) => id !== String(myUserId)) || [];

				if (updated.length === 0) {
					const { assigned, ...rest } = prevFilters;
					newFilters = rest;
				} else {
					newFilters = { ...prevFilters, assigned: updated };
				}
			}

			setQueryParams(wrapQueryParams(newFilters));

			return newFilters;
		});
	};

	useEffect(() => {
		if (isLoadingSamplingHistory || isPendingUsers) return;

		const params = new URLSearchParams({
			...(isActiveSampling && !isCompleteList ? { sampling: '1' } : {}),
			...(project.hasAssignedInvoices && !!myUserId && myUserId !== 'not_found' ? { assigned: myUserId.toString() } : {}),
		});

		setQueryParams(!!params.toString() ? `?${params.toString()}` : null);
	}, [isLoadingSamplingHistory, isActiveSampling, isCompleteList, myUserId, isPendingUsers, project.hasAssignedInvoices]);

	useEffect(() => {
		setActiveFilters(queryParams ? parseQueryParams(queryParams) : {});
	}, [queryParams]);

	const [showModalDownloadReport, setShowModalDownloadReport] = useState(false);

	const localeText = AG_GRID_LOCALE_IT;

	const defaultColDef = {
		resizable: true,
		suppressMovable: true,
		cellStyle: {
			padding: '0 1rem',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
		},
		sortable: true,
		filterParams: {
			suppressAndOrCondition: true, //for disable AND/OR login in filter
			filterOptions: ['contains'],
		},
		suppressCellFocus: false,
		suppressHeaderFocus: false,
		minWidth: 250,
	};

	const { data: totals, isPending: isPendingTotals } = useQuery<{ data: TotalsType }, Error, TotalsType>({
		queryKey: ['totals', projectId],
		queryFn: () => getProjectResumeTotals(projectId),
		select: (resp) => resp.data,
	});

	const [externalSupplies, setExternalSupplies] = useState<ExternalSupplyType[]>([]);
	const [externalSuppliesMeta, setExternalSuppliesMeta] = useState<MetaType>({} as MetaType);

	const [filteredTotal, setFilteredTotal] = useState<null | FilteredTotalType>(null);

	const [statistics, setStatistics] = useState<StatisticsType>();

	// console.log('queryKeys', ['resumeInvoices', projectId, queryParams, 'externalSupplies', isCompleteList, myUserId]);
	const {
		data: resumeInvoices,
		isPending: isPendingResumeInvoices,
		isRefetching: isRefetchingResumeInvoices,
	} = useQuery({
		queryKey: ['resumeInvoices', projectId, queryParams, 'externalSupplies', isCompleteList, myUserId],
		queryFn: () => {
			// console.log('queryKeys in fn', ['resumeInvoices', projectId, queryParams, 'externalSupplies', isCompleteList, myUserId]);
			return getProjectResumeInvoices(projectId, queryParams, 'externalSupplies');
		},
		enabled: queryParams !== undefined && myUserId !== undefined,
	});

	const queryClient = useQueryClient();
	const invalidateResumeInvoicesQuery = () => {
		queryClient.invalidateQueries({
			queryKey: ['resumeInvoices', projectId],
		});
	};

	const hasFetchedOnceRef = useRef(false);

	useEffect(() => {
		if (resumeInvoices && resumeInvoices.success) {
			setExternalSupplies(resumeInvoices.data);
			const { data, success, totals, ...restResp } = resumeInvoices;
			setExternalSuppliesMeta(restResp);
			setStatistics({
				checkAssignedCompleted: totals?.checkAssignedCompleted,
				totalAssignedInvoices: totals?.totalAssignedInvoices,
				percentageAssignedCompleted: totals?.percentageAssignedCompleted,
			});
			if (!hasFetchedOnceRef.current) hasFetchedOnceRef.current = true;

			!!queryParams ? setFilteredTotal(totals) : setFilteredTotal(null);
		}
	}, [resumeInvoices, queryParams]);

	useEffect(() => {
		let timer = null;
		if (isPendingResumeInvoices || isRefetchingResumeInvoices) {
			timer = setTimeout(() => {
				externalSuppliesRef.current?.api?.showLoadingOverlay();
			}, 100);
		} else {
			externalSuppliesRef.current?.api?.hideOverlay();
		}
		return () => clearTimeout(timer);
	}, [isPendingResumeInvoices, isRefetchingResumeInvoices]);

	const { data: trackRecords } = useQuery({
		queryKey: ['trackRecords', projectId],
		queryFn: () => getProjectTrackRecords(projectId),
	});

	const isChecked = queryParams?.replaceAll('[]', '').includes(`assigned=${myUserId}`) || false;

	// const RefreshButton = () => {
	// 	return (
	// 		<OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
	// 			<Button
	// 				onClick={() => {
	// 					invalidateResumeInvoicesQuery();
	// 				}}
	// 				className="btn custom-ghost-button"
	// 				style={{
	// 					fontWeight: 500,
	// 					fontSize: '18px',
	// 					display: 'flex',
	// 					alignItems: 'flex-start',
	// 					justifyContent: 'center',
	// 					gap: '0.5rem',
	// 					textTransform: 'none',
	// 					padding: 0,
	// 				}}
	// 			>
	// 				{false ? (
	// 					<Spinner
	// 						as="span"
	// 						className="mx-1"
	// 						animation="border"
	// 						size="sm"
	// 						role="status"
	// 						aria-hidden="true"
	// 						style={{ verticalAlign: 'middle' }}
	// 					/>
	// 				) : (
	// 					<IcoArrowClockwise />
	// 				)}
	// 				ricarica dati
	// 			</Button>
	// 		</OverlayTrigger>
	// 	);
	// };

	const [openRowId, setOpenRowId] = useState<number | null>(null);

	// const TotalResumeChecklistCellRenderer = (params) => {
	// 	// params.value = reportable
	// 	// VERDE conforme -> tutti verdi (anche green_white e green_white_settled) (nessuna risposta N.D. o NO) BASTA IL FLAG
	// 	// ROSSO non conforme -> sia rossi che gialli (almeno una risposta N.D. o NO) BASTA IL FLAG

	// 	let icon;
	// 	let firstMessage = '';
	// 	// let secondMessage = '';
	// 	const arrayEntities = ['tender', 'frameworkContract', 'contract', 'invoice', 'payment'];

	// 	if (params.value && params.data) {
	// 		const hasSomeGreenWhiteChecklist = arrayEntities.every(
	// 			(entity) => params.data[entity].checkliststatus && params.data[entity]?.checkliststatus.color === 'green_white',
	// 		);
	// 		icon = <ConfirmSolid width={20} height={20} />;
	// 		firstMessage = 'conforme';

	// 		if (hasSomeGreenWhiteChecklist) {
	// 			icon = <IcoConformeDaValidare width={20} height={20} />;
	// 			firstMessage = 'conforme - da validare';
	// 		}
	// 		// firstMessage += hasSomeGreenWhiteChecklist ? ' - da validare' : '';
	// 		// secondMessage = 'visibile in rendicontazione';
	// 	} else {
	// 		const hasAllWhiteChecklist = arrayEntities.every(
	// 			(entity) => params.data[entity]?.checkliststatus && params.data[entity]?.checkliststatus.color === 'white',
	// 		);

	// 		const hasSomeWhiteChecklist = arrayEntities.some(
	// 			(entity) => params.data[entity]?.checkliststatus && params.data[entity]?.checkliststatus.color === 'white',
	// 		);

	// 		if (hasAllWhiteChecklist) {
	// 			icon = <CircleGrey width={20} height={20} />;
	// 			firstMessage = 'da controllare';
	// 			// secondMessage = 'inizia le azioni di controllo';
	// 		} else if (hasSomeWhiteChecklist) {
	// 			icon = <BlackPause width={20} height={20} />;
	// 			firstMessage = 'da completare';
	// 			// secondMessage = 'controlla tutti gli elementi';
	// 		} else {
	// 			icon = <RejectSolid width={20} height={20} />;
	// 			firstMessage = 'non conforme';
	// 			// secondMessage = 'non visibile in rendicontazione';
	// 		}
	// 	}
	// 	const secondMessage = !params.data.sampled ? 'singolo controllo' : 'post-campionamento';

	// 	return (
	// 		<Row className="align-items-center justify-content-center h-100">
	// 			<Col className="col-auto text-center">{icon}</Col>
	// 			<Col>
	// 				<span className="f-700 d-block">{firstMessage}</span>
	// 				<span>{secondMessage}</span>
	// 			</Col>
	// 		</Row>
	// 	);
	// };

	const ReportedCheckRenderer = (params) => {
		const isReporting = params.data.reporting;
		const isReported = params.value;
		return (
			<div
				style={{
					display: 'grid',
					placeContent: 'center',
					height: '100%',
				}}
			>
				{isReporting ? <IcoReportedPartially /> : <></>}
				{isReported ? <IcoReported /> : <></>}
			</div>
		);
	};

	const hasAdvancedConfig = (configKey: string) => {
		return (
			Array.isArray(project.advancedConfig) && !!project.advancedConfig.find((advConf) => advConf.name === configKey && advConf.enabled)
		);
	};

	const hasSamplingActivity = hasAdvancedConfig('sampling_activity');

	const hasInfratelActivity = hasAdvancedConfig('infratel_activity');

	const hasSampledActivity = externalSupplies.some((supply) => supply['sampled'] === true);

	const originalWidths = useRef<Record<string, number>>({});

	const saveOriginalWidths = useCallback(() => {
		const columnApi = externalSuppliesRef.current?.columnApi;
		if (!columnApi) return;

		columnApi.getAllColumns()?.forEach((col) => {
			originalWidths.current[col.getColId()] = col.getActualWidth();
		});
	}, []);
	const restoreWidths = useCallback(() => {
		const columnApi = externalSuppliesRef.current?.columnApi;
		if (!columnApi) return;

		Object.entries(originalWidths.current).forEach(([colId, width]) => {
			columnApi.setColumnWidth(colId, width, false);
		});
	}, []);
	const updateGridLayout = useCallback(() => {
		const api = externalSuppliesRef.current?.api;
		if (!api) return;
		const FIT_THRESHOLD = 1920;

		if (window.innerWidth > FIT_THRESHOLD) {
			saveOriginalWidths();
			api.sizeColumnsToFit();
		} else {
			restoreWidths();
		}
	}, [restoreWidths, saveOriginalWidths]);

	useEffect(() => {
		window.addEventListener('resize', updateGridLayout);
		return () => window.removeEventListener('resize', updateGridLayout);
	}, [updateGridLayout]);

	const columnDefsExternalSupplies = [
		{
			headerName: '',
			field: 'reported',
			minWidth: 56,
			width: 56,
			maxWidth: 56,
			cellRenderer: ReportedCheckRenderer,
			...columnCentered(),
		},
		...(project.streamConfigurationType === 'STREAM'
			? [
					...(!hasInfratelActivity
						? [
								{
									headerName: 'Procedura',
									field: 'tender',
									cellRenderer: ResumeChecklistCellRenderer,
									cellRendererParams: {
										project,
										modalComponent: SummaryTenderModal,
									},
									filter: 'agTextColumnFilter',
									filterValueGetter: (params) => {
										let value;

										if (params.data.tender.keyValue.startsWith('GARA_')) {
											value = 'N.A.';
										} else if (params.data.tender.keyValue.startsWith('GARATEMP_')) {
											value = 'N.D.';
										} else {
											value = params.data.tender.keyValue;
										}

										return value;
									},
									comparator: (valueA, valueB) => {
										let tempValueA = valueA.keyValue.startsWith('GARA_')
											? 'N.A.'
											: valueA.keyValue.startsWith('GARATEMP_')
												? 'N.D.'
												: valueA.keyValue;
										let tempValueB = valueB.keyValue.startsWith('GARA_')
											? 'N.A.'
											: valueB.keyValue.startsWith('GARATEMP_')
												? 'N.D.'
												: valueB.keyValue;

										if (tempValueA === tempValueB) return 0;
										return tempValueA > tempValueB ? 1 : -1;
									},
									...columnCentered(),
								},
							]
						: []),
					{
						headerName: 'Contratto aperto',
						field: 'frameworkContract',
						cellRenderer: ResumeChecklistCellRenderer,
						cellRendererParams: {
							project,
							modalComponent: SummaryFrameworkcontractModal,
						},
						filter: 'agTextColumnFilter',
						filterValueGetter: (params) => {
							return params.data.frameworkContract.keyValue;
						},
						comparator: (valueA, valueB) => {
							if (valueA.keyValue === valueB.keyValue) return 0;
							return valueA.keyValue > valueB.keyValue ? 1 : -1;
						},
						...columnCentered(),
					},
				]
			: []),
		{
			headerName: 'Incarico/attingimento',
			field: 'contract',
			cellRenderer: ResumeChecklistCellRenderer,
			cellRendererParams: {
				project,
				modalComponent: SummaryContractModal,
			},
			filter: 'agTextColumnFilter',
			filterValueGetter: (params) => {
				return params.data.contract.keyValue;
			},
			comparator: (valueA, valueB) => {
				if (valueA.keyValue === valueB.keyValue) return 0;
				return valueA.keyValue > valueB.keyValue ? 1 : -1;
			},
			...columnCentered(),
		},
		{
			headerName: 'Fattura',
			field: 'invoice',
			cellRenderer: ResumeChecklistCellRenderer,
			cellRendererParams: {
				project,
				modalComponent: SummaryInvoiceModal,
			},
			filter: 'agTextColumnFilter',
			filterValueGetter: (params) => {
				return params.data.invoice.keyValue;
			},
			comparator: (valueA, valueB) => {
				if (valueA.keyValue === valueB.keyValue) return 0;
				return valueA.keyValue > valueB.keyValue ? 1 : -1;
			},
			...columnCentered(),
		},
		{
			headerName: 'Pagamento',
			field: 'payment',
			cellRenderer: ResumeChecklistCellRenderer,
			cellRendererParams: {
				project,
				modalComponent: SummaryPaymentModal,
			},
			filter: 'agTextColumnFilter',
			filterValueGetter: (params) => {
				return params.data.payment.keyValue;
			},
			comparator: (valueA, valueB) => {
				if (valueA.keyValue === valueB.keyValue) return 0;
				return valueA.keyValue > valueB.keyValue ? 1 : -1;
			},
			...columnCentered(),
		},
		{
			headerName: 'Ownership',
			field: 'ownership',
			filter: 'agTextColumnFilter',
			cellRendererParams: {
				queryParam: 'assigned',
				invalidateQuery: invalidateResumeInvoicesQuery,
			},
			comparator: (valueA, valueB) => {
				if (valueA.keyValue === valueB.keyValue) return 0;
				return valueA.keyValue > valueB.keyValue ? 1 : -1;
			},
			cellRenderer: (params) => {
				return <OwnershipCellRenderer {...params} />;
			},
			...columnCentered(),
			cellStyle: {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
			},
			cellClass: 'cell-dropdown',
		},
		// ...(!hasInfratelActivity
		// ? [
		{
			headerName: 'Importo inerente',
			field: 'totalCost',
			valueFormatter: (params) => formatToDecimals(params.data.totalCost) + ' €',
			...columnCentered(),
			cellStyle: {
				display: 'grid',
				placeContent: 'center',
			},
		},
		{
			headerName: 'Importo rendicontabile',
			field: 'totalEligibleAmount',
			valueFormatter: (params) => formatToDecimals(params.data.totalEligibleAmount) + ' €',
			...columnCentered(),
			cellStyle: {
				display: 'grid',
				placeContent: 'center',
			},
		},
		// ]
		// : []),
		...(project.streamConfigurationType === 'WP'
			? [
					{
						headerName: 'Beneficiario',
						field: 'beneficiary.name',
						minWidth: 170,
						filter: 'agTextColumnFilter',
						filterValueGetter: (params) => {
							return params.data.beneficiary.name;
						},
						...columnCentered(),
						cellStyle: {
							display: 'grid',
							placeContent: 'center',
						},
					},
				]
			: []),
		{
			headerName: 'Stato del controllo',
			field: 'reportable',
			cellRenderer: (params) => {
				return <TotalResumeChecklistCellRenderer {...params} />;
			},
			// cellRendererParams: {
			// 	queryParam: 'reportable',
			// },
			minWidth: 301,
			...columnCentered(),
			cellStyle: {
				whiteSpace: 'initial',
				justifyContent: 'center',
			},
			pinned: 'right',
			suppressSizeToFit: true,
		},
	];

	const options = [
		{
			value: 'white',
			label: 'Da lavorare',
		},
		{
			value: 'gray',
			label: 'In lavorazione',
		},
		{
			value: 'red',
			label: 'Non conforme',
		},
		{
			value: 'yellow',
			label: 'Incompleta',
		},
		{
			value: 'green',
			label: 'Conforme',
		},
		{
			value: 'green_white',
			label: 'Conforme - da validare',
		},
		{
			value: 'green_white_settled',
			label: 'Conforme - validato',
		},
	];

	const statusOption = {
		name: 'status',
		label: 'Stato',
		options,
		type: 'static' as const,
		needsEntityType: true,
	};

	const hasDocumentsOption = {
		name: 'has_document',
		label: 'Contiene documenti',
		switchLabelPrefix: 'Filtra soltanto',
		switchLabelSuffix: 'per documenti e allegati',
		type: 'boolean' as const,
		options: [
			{
				value: '1',
				label: 'Presenti',
			},
			{
				value: '0',
				label: 'Assenti',
			},
			{
				value: 'none',
				label: 'Nessun filtro',
			},
		],
	};

	const hasAvvisoOption = {
		name: 'has_document',
		extraQueryParams: [
			{
				name: 'document_codes',
				value: 'PA_AVVISO',
			},
		],
		label: 'Avviso di pagamento',
		switchLabelPrefix: 'Filtra per',
		switchLabelSuffix: 'con avviso',
		type: 'boolean-extra' as const,
		options: [
			{
				value: '1',
				label: 'Presente',
			},
			{
				value: '0',
				label: 'Assente',
			},
			{
				value: 'none',
				label: 'Nessun filtro',
			},
		],
	};

	const hasGiustificativoOption = {
		name: 'has_document',
		extraQueryParams: [
			{
				name: 'document_codes',
				value: 'PA_GIUSTIFICATIVO',
			},
		],
		label: 'Giustificativo di pagamento',
		switchLabelPrefix: 'Filtra per',
		switchLabelSuffix: 'con giustificativo',
		type: 'boolean-extra' as const,
		options: [
			{
				value: '1',
				label: 'Presente',
			},
			{
				value: '0',
				label: 'Assente',
			},
			{
				value: 'none',
				label: 'Nessun filtro',
			},
		],
	};

	const reportableOptions = [
		{
			value: '1',
			label: 'Eleggibili',
		},
		{
			value: '0',
			label: 'Non eleggibili',
		},
		{
			value: 'none',
			label: 'Nessun filtro',
		},
	];

	const reportableOption = {
		name: 'reportable',
		label: 'Catene documentali',
		type: 'boolean' as const,
		options: reportableOptions,
	};

	const sampledOptions = [
		{
			value: '1',
			label: 'Campionati',
		},
		{
			value: '0',
			label: 'Non campionati',
		},
		{
			value: 'none',
			label: 'Nessun filtro',
		},
	];

	const sampledOption = {
		name: 'sampled',
		label: 'Campionamento',
		type: 'boolean' as const,
		options: sampledOptions,
	};
	const controlOption = {
		name: 'control',
		label: 'Domanda contenuta',
		type: 'control' as const,
		needsEntityType: true,
	};
	const userOptions =
		users?.map((user) => ({
			value: `${user.id}`,
			label: `${user.firstName} ${user.lastName}`,
		})) ?? [];

	const trackRecordOptions =
		trackRecords?.map((record) => ({
			value: `${record.id}`,
			label: `${record.name}`,
		})) ?? [];

	const filterFields = [
		{
			name: 'ownership',
			label: 'Ownership',
			queryParamName: 'assigned',
			options: [{ value: 'none', label: 'Non assegnate' }, ...userOptions],
			searchCheckboxAdvancedFilter: true,
			searchCheckboxPlaceholder: 'Cerca una persona...',
			optionsType: 'dynamic' as const,
		},
		...(!hasInfratelActivity
			? [
					{
						name: 'tender',
						label: 'Procedura',
						options,
						queryParamName: 'tenders_color',
						entityType: 'tenders',
						needsEntityType: true,
						advancedOptions: [statusOption, controlOption, hasDocumentsOption],
						pluralLabel: 'procedure',
						optionsType: 'static' as const,
					},
				]
			: []),
		{
			name: 'frameworkContract',
			label: 'Contratto aperto',
			options,
			queryParamName: 'frameworkcontracts_color',
			entityType: 'frameworkcontracts',
			needsEntityType: true,
			advancedOptions: [statusOption, controlOption, hasDocumentsOption],
			pluralLabel: 'contratti aperti',
			optionsType: 'static' as const,
		},
		{
			name: 'contract',
			label: 'Incarico/Attingimento',
			options,
			queryParamName: 'contracts_color',
			entityType: 'contracts',
			needsEntityType: true,
			advancedOptions: [statusOption, controlOption, hasDocumentsOption],
			pluralLabel: 'incarichi/attingimenti',
			optionsType: 'static' as const,
		},
		{
			name: 'invoice',
			label: 'Fattura',
			options,
			queryParamName: 'invoices_color',
			entityType: 'invoices',
			needsEntityType: true,
			advancedOptions: [statusOption, controlOption, hasDocumentsOption],
			pluralLabel: 'fatture',
			optionsType: 'static' as const,
		},
		{
			name: 'invoicepayment',
			label: 'Pagamento',
			options,
			queryParamName: 'invoicepayments_color',
			entityType: 'invoicepayments',
			needsEntityType: true,
			advancedOptions: [statusOption, controlOption, hasAvvisoOption, hasGiustificativoOption],
			pluralLabel: 'pagamenti',
			optionsType: 'static' as const,
		},
		{
			name: 'reportable',
			label: 'Catene documentali',
			queryParamName: 'reportable',
			type: 'boolean' as const,
			options: reportableOptions,
			advancedOptions: [reportableOption],
			booleanLabel: 'Elementi eleggibili',
		},
		...(hasInfratelActivity || (hasSamplingActivity && hasSampledActivity && isCompleteList)
			? [
					{
						name: 'sampled',
						label: 'Campionamento',
						queryParamName: 'sampled',
						type: 'boolean' as const,
						options: sampledOptions,
						advancedOptions: [sampledOption],
						booleanLabel: 'Elementi campionati',
					},
				]
			: []),
		...(trackRecordOptions && trackRecordOptions.length > 0
			? [
					{
						name: 'trackRecord',
						label: 'Track Record',
						queryParamName: 'trackId',
						options: trackRecordOptions,
						searchCheckboxAdvancedFilter: true,
						searchCheckboxPlaceholder: 'Cerca un track record...',
						optionsType: 'dynamic' as const,
					},
				]
			: []),
	];

	return (
		<Fragment>
			{userAccess.isAboveAuditor || userAccess.isGuestEnteErogante ? (
				<Row
					className="align-items-center mb-3"
					style={{
						marginTop: '13px',
					}}
				>
					{hasSamplingActivity ? (
						isLoadingSamplingHistory ? (
							<Loading fixedHeight="50vh" />
						) : (
							<>
								<Row>
									<Col>
										{!isActiveSampling ? (
											<SamplingSetupModal
												invoicesLength={externalSupplies.length}
												project={project}
												invalidateQuery={invalidateResumeInvoicesQuery}
											/>
										) : !isCompleteList ? (
											<SamplingInfoBox
												invalidateQuery={invalidateResumeInvoicesQuery}
												resetQueryParams={resetQueryParams}
												project={project}
											/>
										) : (
											<Button
												onClick={() => {
													history.push(`${pathname.replace(completeListParam, '')}`);
													resetQueryParams();
												}}
												className="btn custom-ghost-button ms-auto no-underline"
											>
												<ArrowRight
													className="me-2 ico-path-fill-primary"
													style={{
														rotate: '180deg',
													}}
												/>
												<span style={{ color: '#1C2743', textTransform: 'none', fontWeight: '500', verticalAlign: 'middle' }}>
													Torna al campionamento
												</span>
											</Button>
										)}
									</Col>
								</Row>
							</>
						)
					) : null}

					{canViewMacroInfo && !isActiveSampling ? (
						isPendingTotals ? (
							<Loading fixedHeight="180px" />
						) : (
							<Row style={{ marginTop: '40px' }}>
								<Col>
									<ExecutionSummaryCard
										title="Avanzamento"
										items={[
											{
												key: 'total',
												label: 'Spesa effettuata:',
												value: formatToDecimals(totals.total) + ' €',
											},
											{
												key: 'eligible',
												label: 'Di cui eleggibile:',
												value: formatToDecimals(totals.totalRend) + ' €',
											},
										]}
									/>
								</Col>
								<Col>
									<ExecutionSummaryCard
										title="Costi Esterni"
										items={[
											{
												key: 'external-supplies-expenses',
												label: 'Spesa in costi esterni:',
												value: formatToDecimals(totals.totalFt) + ' €',
											},
											{
												key: 'external-supplies-eligible',
												label: 'Di cui eleggibile:',
												value: formatToDecimals(totals.totalRendFt) + ' €',
											},
										]}
									/>
								</Col>
								<Col>
									<ExecutionSummaryCard
										title="Filtro"
										items={[
											{
												key: 'external-supplies-filtered',
												label: 'Spesa filtrata:',
												value: `${!!filteredTotal ? formatToDecimals(filteredTotal.all) : formatToDecimals(totals.totalFt)} €`,
											},
											{
												key: 'external-supplies-filtered-eligible',
												label: 'Di cui eleggibile:',
												value: `${!!filteredTotal ? formatToDecimals(filteredTotal.rend) : formatToDecimals(totals.totalRendFt)} €`,
											},
										]}
									/>
								</Col>
								<Col>
									<ExecutionSummaryProgressCard statistics={statistics} />
								</Col>
							</Row>
						)
					) : null}

					<Row
						style={{
							marginBlock: '40px',
						}}
					>
						<Col>
							<span style={{ fontWeight: 700 }}>Fai click sugli elementi nelle celle per eseguire le azioni di controllo.</span>
							<br />
							Fai click sugli elementi nelle celle per eseguire le azioni di controllo.
						</Col>
						<Col
							style={{
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<div
								style={{
									marginLeft: 'auto',
								}}
							>
								<OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
									<Button
										onClick={() => {
											invalidateResumeInvoicesQuery();
										}}
										className="btn custom-ghost-button"
										style={{
											fontWeight: 500,
											fontSize: '18px',
											display: 'flex',
											alignItems: 'flex-start',
											justifyContent: 'center',
											gap: '0.5rem',
											textTransform: 'none',
											padding: 0,
										}}
									>
										{false ? (
											<Spinner
												as="span"
												className="mx-1"
												animation="border"
												size="sm"
												role="status"
												aria-hidden="true"
												style={{ verticalAlign: 'middle' }}
											/>
										) : (
											<IcoArrowClockwise />
										)}
										ricarica dati
									</Button>
								</OverlayTrigger>
							</div>
						</Col>
					</Row>
					<Row
						style={{
							marginBottom: '2rem',
						}}
					>
						<Col
							className="col-4"
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '2rem',
							}}
						>
							<OwnershipModal
								disabled={isLoadingSamplingHistory || isPendingResumeInvoices}
								// externalSuppliesCount={externalSuppliesMeta?.meta?.total || 0}
								invalidateQuery={invalidateResumeInvoicesQuery}
								samplingId={samplingHistory?.id}
							/>

							<Form.Check
								className="me-3 new custom-switch"
								inline
								reverse
								label={'assegnate a me'}
								type="switch"
								onChange={handleAssignedToMe}
								checked={isChecked}
							/>
						</Col>
						<Col className="col-8 text-right py-1">
							<ButtonToolbar className="justify-content-end align-items-center" style={{ height: '38px', gap: '1.5rem' }}>
								<CustomSearch
									ref={searchRef}
									isLoading={hasFetchedOnceRef.current && (isPendingResumeInvoices || isRefetchingResumeInvoices)}
									activeFilters={activeFilters}
									setActiveFilters={setActiveFilters}
									setQueryParams={setQueryParams}
								/>

								<CustomFilter
									activeFilters={activeFilters}
									setActiveFilters={setActiveFilters}
									queryParams={queryParams}
									setQueryParams={setQueryParams}
									pageName="Controllo"
									projectName={project.name}
									filterFields={filterFields}
									hasNewButton
								/>

								<Button
									onClick={() => {
										setShowModalDownloadReport(true);
									}}
									className="btn custom-ghost-button"
									style={{
										fontWeight: 500,
										fontSize: '18px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '0.5rem',
										textTransform: 'none',
										padding: 0,
									}}
								>
									<IcoDownloadCloudArrowSmall fill="#1C2743" />

									<span style={{ translate: '0 1px' }}>scarica</span>
								</Button>

								<ModalDownloadReport
									filterQueryParamsString={queryParams}
									projectId={projectId}
									setShowModalDownloadReport={setShowModalDownloadReport}
									showModalDownloadReport={showModalDownloadReport}
									hasMissingReport
								/>
							</ButtonToolbar>
						</Col>
					</Row>
					<Row>
						<FiltersToDisplay
							queryParams={queryParams}
							setQueryParams={setQueryParams}
							filterFields={filterFields}
							myUser={myUser}
							myUserId={`${myUserId}`}
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							searchRef={searchRef}
						/>
					</Row>
				</Row>
			) : null}
			<Row>
				{Array.isArray(externalSupplies) ? (
					<Col className="col-12 col-xxl-9" id="grid-wrapper">
						<div
							className="custom-pinned_right-table ag-theme-alpine list-table new mb-4"
							style={{ height: '800px', position: 'relative' }}
						>
							{/* N.D.R.: hack for header + cell box-shadow */}
							<div
								style={{
									position: 'absolute',
									top: 0,
									bottom: 0,
									right: 0,
									zIndex: 999,
									boxShadow: '-10px 0px 10px -5px #0000004d',
									width: externalSupplies.length > 6 ? '315px' : '300px',
									pointerEvents: 'none',
									transition: 'width 0.3s ease-in-out',
								}}
							/>
							<AgGridReact
								ref={externalSuppliesRef}
								columnDefs={columnDefsExternalSupplies}
								rowData={externalSupplies}
								defaultColDef={defaultColDef}
								tooltipShowDelay={0}
								tooltipHideDelay={2000}
								tooltipMouseTrack={true}
								loadingOverlayComponent={() => <Loading />}
								headerHeight={60}
								rowHeight={104}
								suppressCellFocus={true}
								rowSelection="single"
								localeText={localeText}
								context={{
									openRowId,
									setOpenRowId,
								}}
								onFirstDataRendered={updateGridLayout}
							/>
							{Object.keys(externalSuppliesMeta).length > 0 && (
								<CustomPaginationMui
									dataMeta={externalSuppliesMeta}
									setData={setExternalSupplies}
									setDataMeta={setExternalSuppliesMeta}
									refTable="externalSupplies"
									setIsFirstResearch={() => {}}
									setIsChangedPagination={() => {}}
								/>
							)}
						</div>
					</Col>
				) : (
					<Loading fixedHeight="50vh" />
				)}
			</Row>
		</Fragment>
	);
};

export default SummaryExternalSupplies;
